import type { ChannelDefinition, ChannelId } from '../types/channel.js';

export const CHANNEL_REGISTRY: readonly ChannelDefinition[] = [
  { id: 'slack', name: 'Slack', securityTier: 'standard', e2eEncrypted: false, auditTrail: true, publicExposureRisk: 'Low' },
  { id: 'msteams', name: 'Microsoft Teams', securityTier: 'standard', e2eEncrypted: false, auditTrail: true, publicExposureRisk: 'Low' },
  { id: 'telegram', name: 'Telegram', securityTier: 'standard', e2eEncrypted: 'optional', auditTrail: 'partial', publicExposureRisk: 'Medium' },
  // WhatsApp is wired through a Business-Platform provider (Kapso -> Meta Cloud
  // API, ENG-6812), NOT consumer WhatsApp. That path terminates end-to-end
  // encryption at Meta and the message content also transits the provider, so
  // for our purposes it is a standard TLS-transport channel, not an E2E
  // elevated one. Classifying it elevated / e2eEncrypted:true would let the
  // channel-policy lint (PII-on-limited / require-elevated-for-pii) reason on a
  // false premise. See docs/research/eng-6810-kapso-whatsapp-integration.md.
  { id: 'whatsapp', name: 'WhatsApp', securityTier: 'standard', e2eEncrypted: false, auditTrail: false, publicExposureRisk: 'Medium' },
  { id: 'signal', name: 'Signal', securityTier: 'elevated', e2eEncrypted: true, auditTrail: false, publicExposureRisk: 'Low' },
  { id: 'discord', name: 'Discord', securityTier: 'limited', e2eEncrypted: false, auditTrail: false, publicExposureRisk: 'High' },
  { id: 'irc', name: 'IRC', securityTier: 'limited', e2eEncrypted: false, auditTrail: false, publicExposureRisk: 'High' },
  { id: 'matrix', name: 'Matrix', securityTier: 'standard', e2eEncrypted: 'optional', auditTrail: true, publicExposureRisk: 'Medium' },
  { id: 'mattermost', name: 'Mattermost', securityTier: 'standard', e2eEncrypted: false, auditTrail: true, publicExposureRisk: 'Low' },
  { id: 'imessage', name: 'iMessage', securityTier: 'elevated', e2eEncrypted: true, auditTrail: false, publicExposureRisk: 'Low' },
  { id: 'google-chat', name: 'Google Chat', securityTier: 'standard', e2eEncrypted: false, auditTrail: true, publicExposureRisk: 'Low' },
  { id: 'nostr', name: 'Nostr', securityTier: 'limited', e2eEncrypted: 'optional', auditTrail: false, publicExposureRisk: 'High' },
  { id: 'line', name: 'LINE', securityTier: 'standard', e2eEncrypted: 'optional', auditTrail: 'partial', publicExposureRisk: 'Medium' },
  { id: 'feishu', name: 'Feishu', securityTier: 'standard', e2eEncrypted: false, auditTrail: true, publicExposureRisk: 'Low' },
  { id: 'nextcloud-talk', name: 'Nextcloud Talk', securityTier: 'standard', e2eEncrypted: 'optional', auditTrail: true, publicExposureRisk: 'Low' },
  { id: 'zalo', name: 'Zalo', securityTier: 'standard', e2eEncrypted: false, auditTrail: 'partial', publicExposureRisk: 'Medium' },
  { id: 'tlon', name: 'Tlon', securityTier: 'standard', e2eEncrypted: true, auditTrail: true, publicExposureRisk: 'Low' },
  { id: 'bluebubbles', name: 'BlueBubbles', securityTier: 'limited', e2eEncrypted: false, auditTrail: false, publicExposureRisk: 'Low' },
  { id: 'beam', name: 'Beam Protocol', securityTier: 'elevated', e2eEncrypted: true, auditTrail: true, publicExposureRisk: 'Low' },
  { id: 'direct-chat', name: 'Direct Chat', securityTier: 'standard', e2eEncrypted: false, auditTrail: true, publicExposureRisk: 'Low' },
  { id: 'grok-voice', name: 'Grok Voice', securityTier: 'standard', e2eEncrypted: false, auditTrail: true, publicExposureRisk: 'Medium' },
] as const;

const channelMap = new Map<string, ChannelDefinition>(
  CHANNEL_REGISTRY.map((c) => [c.id, c]),
);

export function getChannel(id: string): ChannelDefinition | undefined {
  return channelMap.get(id);
}

export function getAllChannelIds(): ChannelId[] {
  return CHANNEL_REGISTRY.map((c) => c.id);
}

/**
 * Sensible default set of channels enabled for a brand-new org's channel
 * policy (ENG-5790). Seeded at org creation by the `seed_default_channel_policy`
 * trigger and pre-selected in the onboarding channel step, so downstream
 * pickers (announcement channel, agent channel bindings) are never
 * empty/all-disabled.
 *
 * These are the mainstream, generally-available channels plus the always-on
 * `direct-chat` baseline. `direct-chat` MUST stay in the default: a non-empty
 * org allow-list is intersected with each agent's effective channels by
 * `resolveChannels`, so omitting it would silently strip console DM from every
 * agent. Coming-soon channels (discord / whatsapp / imessage) are deliberately
 * left off — an admin enables those explicitly.
 *
 * Keep in sync with the `ARRAY[...]` literal in the seed migration
 * (`*_seed_default_channel_policy.sql`).
 */
export const DEFAULT_ORG_ALLOWED_CHANNELS: readonly ChannelId[] = [
  'slack',
  'telegram',
  'msteams',
  'grok-voice',
  'direct-chat',
] as const;
